import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para adicionar token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Não sobrescrever Content-Type se for FormData
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Interceptor para tratar respostas e detectar token inválido/expirado
api.interceptors.response.use(
  (response) => {
    // Resposta bem-sucedida
    return response;
  },
  (error) => {
    // Verificar se é erro de autenticação (401 ou 403)
    if (error.response) {
      const status = error.response.status;
      const data = error.response.data;

      // Detectar erros de autenticação para tokens expirados
      // IMPORTANTE: Não redirecionar se estiver tentando fazer login ou desafio 2FA
      const isLoginRequest = error.config?.url?.includes('/auth/login');
      const is2FAChallenge = error.config?.url?.includes('/auth/2fa/challenge');

      if ((status === 401 || status === 403) && !isLoginRequest && !is2FAChallenge) {
        const isAuthError =
          data?.error?.includes('Não autorizado') ||
          data?.error?.includes('Token') ||
          data?.error?.includes('autenticação') ||
          data?.message?.includes('Token') ||
          data?.message?.includes('autenticação') ||
          status === 401;

        if (isAuthError) {
          console.warn('Token expirado ou inválido detectado. Redirecionando para login...');

          // Limpar dados de autenticação
          localStorage.removeItem('user');
          localStorage.removeItem('token');
          localStorage.removeItem('redirectAfterLogin');

          // Redirecionar para login se não estiver já na página de login
          if (!window.location.pathname.includes('/login')) {
            const basename = process.env.PUBLIC_URL || '/';
            window.location.href = `${basename}/login`;
          }
        }
      }
    }

    return Promise.reject(error);
  }
);

export const authService = {
  login: async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  },
  validate: async () => {
    const response = await api.get('/auth/validate');
    return response.data;
  },
  logout: async () => {
    const response = await api.post('/auth/logout');
    return response.data;
  },
  updateProfile: async (data) => {
    const response = await api.put('/auth/profile', data);
    return response.data;
  },
  changePassword: async (data) => {
    const response = await api.put('/auth/password', data);
    return response.data;
  },
  setup2FA: async () => {
    const response = await api.post('/auth/2fa/setup');
    return response.data;
  },
  confirm2FA: async (data) => {
    const response = await api.post('/auth/2fa/confirm', data);
    return response.data;
  },
  disable2FA: async (data) => {
    const response = await api.post('/auth/2fa/disable', data);
    return response.data;
  },
  challenge2FA: async (data) => {
    const response = await api.post('/auth/2fa/challenge', data);
    return response.data;
  },
  get2FAStatus: async () => {
    const response = await api.get('/auth/2fa/status');
    return response.data;
  }
};

export const usersService = {
  getAll: async () => {
    const response = await api.get('/users');
    return response.data;
  },
  create: async (data) => {
    const response = await api.post('/users', data);
    return response.data;
  },
  update: async (id, data) => {
    const response = await api.put(`/users/${id}`, data);
    return response.data;
  },
  remove: async (id) => {
    const response = await api.delete(`/users/${id}`);
    return response.data;
  }
};

export const campaignsService = {
  getAll: async (params = {}) => {
    const response = await api.get('/campaigns', { params });
    return response.data;
  },
  getById: async (id) => {
    const response = await api.get(`/campaigns/${id}`);
    return response.data;
  },
  create: async (data) => {
    const response = await api.post('/campaigns', data);
    return response.data;
  },
  update: async (id, data) => {
    const response = await api.put(`/campaigns/${id}`, data);
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/campaigns/${id}`);
    return response.data;
  },
  start: async (id) => {
    const response = await api.post(`/campaigns/${id}/start`);
    return response.data;
  },
  pause: async (id) => {
    const response = await api.post(`/campaigns/${id}/pause`);
    return response.data;
  },
  restart: async (id) => {
    const response = await api.post(`/campaigns/${id}/restart`);
    return response.data;
  },
  clearResponses: async (id) => {
    const response = await api.post(`/campaigns/${id}/clear-responses`);
    return response.data;
  }
};

export const customersService = {
  getAll: async (params = {}) => {
    const response = await api.get('/customers', { params });
    return response.data;
  },
  getById: async (id) => {
    const response = await api.get(`/customers/${id}`);
    return response.data;
  },
  create: async (data) => {
    const response = await api.post('/customers', data);
    return response.data;
  },
  update: async (id, data) => {
    const response = await api.put(`/customers/${id}`, data);
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/customers/${id}`);
    return response.data;
  },
  importCsv: async (data) => {
    const response = await api.post('/customers/import', data);
    return response.data;
  },
  importCustomers: async (data) => {
    const response = await api.post('/customers/import-customers', data);
    return response.data;
  },
  getGroups: async () => {
    const response = await api.get('/customers/groups');
    return response.data;
  }
};

export const dispatchesService = {
  getAll: async () => {
    const response = await api.get('/dispatches');
    return response.data;
  },
  create: async (data) => {
    const response = await api.post('/dispatches', data);
    return response.data;
  },
  update: async (id, data) => {
    const response = await api.put(`/dispatches/${id}`, data);
    return response.data;
  },
  start: async (id) => {
    const response = await api.post(`/dispatches/${id}/start`);
    return response.data;
  },
  pause: async (id) => {
    const response = await api.post(`/dispatches/${id}/pause`);
    return response.data;
  },
  restart: async (id) => {
    const response = await api.post(`/dispatches/${id}/restart`);
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/dispatches/${id}`);
    return response.data;
  }
};

export const responsesService = {
  getAll: async (params = {}) => {
    const response = await api.get('/responses', { params });
    return response.data;
  },
  getById: async (id) => {
    const response = await api.get(`/responses/${id}`);
    return response.data;
  },
  getMetrics: async (campaignId) => {
    const response = await api.get('/responses/metrics', { params: { campaign_id: campaignId } });
    return response.data;
  },
  getCampaignMetrics: async (campaignId) => {
    const response = await api.get('/responses/campaign-metrics', { params: { campaign_id: campaignId } });
    return response.data;
  },
  export: async (params = {}, format = 'csv') => {
    // Usar fetch diretamente para download de arquivo
    const queryParams = new URLSearchParams({ ...params, format });
    const response = await fetch(`${api.defaults.baseURL}/responses/export?${queryParams}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Erro ao exportar' }));
      throw new Error(error.message || 'Erro ao exportar respostas');
    }

    // Obter nome do arquivo do header ou usar padrão
    const contentDisposition = response.headers.get('content-disposition');
    let filename = `respostas_${new Date().toISOString().split('T')[0]}.csv`;
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return { success: true, filename };
  }
};

export const dataSyncService = {
  getIntegrations: async () => {
    const response = await api.get('/datasync/integrations');
    return response.data;
  },
  createOrUpdateIntegration: async (data) => {
    const response = await api.post('/datasync/integrations', data);
    return response.data;
  },
  updateIntegration: async (id, data) => {
    const response = await api.put(`/datasync/integrations/${id}`, data);
    return response.data;
  }
};

export const whatsappService = {
  getSettings: async () => {
    const response = await api.get('/whatsapp/settings');
    return response.data;
  },
  updateSettings: async (data) => {
    const response = await api.put('/whatsapp/settings', data);
    return response.data;
  },
  testConnection: async (data) => {
    const response = await api.post('/whatsapp/test-connection', data);
    return response.data;
  },
  validateToken: async () => {
    const response = await api.get('/whatsapp/validate-token');
    return response.data;
  },
  uploadMedia: async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post('/whatsapp/media', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },
  sendReply: async (data) => {
    const response = await api.post('/whatsapp/send-reply', data);
    return response.data;
  },
  // Gerenciamento de múltiplas contas
  getAccounts: async () => {
    const response = await api.get('/whatsapp/accounts');
    return response.data;
  },
  addAccount: async (data) => {
    const response = await api.post('/whatsapp/accounts', data);
    return response.data;
  },
  activateAccount: async (id) => {
    const response = await api.post(`/whatsapp/accounts/${id}/activate`);
    return response.data;
  },
  updateAccount: async (id, data) => {
    const response = await api.put(`/whatsapp/accounts/${id}`, data);
    return response.data;
  },
  removeAccount: async (id) => {
    const response = await api.delete(`/whatsapp/accounts/${id}`);
    return response.data;
  },
  testMessage: async (phoneNumber, templateName, language = 'pt_BR') => {
    console.log('=== DEBUG testMessage ===');
    console.log('Phone:', phoneNumber);
    console.log('Template:', templateName);
    console.log('Language:', language);

    const payload = {
      phone_number: phoneNumber,
      template_name: templateName,
      language: language
    };

    console.log('Payload enviado:', payload);

    const response = await api.post('/whatsapp/test-message', payload);

    console.log('Resposta do backend:', response.data);
    return response.data;
  }
};

export const wabaTemplatesService = {
  getAll: async () => {
    const response = await api.get('/whatsapp/templates');
    return response.data;
  },
  syncFromMeta: async () => {
    const response = await api.post('/whatsapp/templates/sync');
    return response.data;
  },
  getById: async (id) => {
    const response = await api.get(`/whatsapp/templates/${id}`);
    return response.data;
  },
  create: async (data) => {
    const response = await api.post('/whatsapp/templates', data);
    return response.data;
  },
  update: async (id, data) => {
    const response = await api.put(`/whatsapp/templates/${id}`, data);
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/whatsapp/templates/${id}`);
    return response.data;
  },
  uploadMedia: async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post('/whatsapp/media', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  }
};

export const contactRequestService = {
  create: async (data) => {
    const response = await api.post('/contact-requests', data);
    return response.data;
  },
  getAll: async (params = {}) => {
    const response = await api.get('/contact-requests', { params });
    return response.data;
  },
  getById: async (id) => {
    const response = await api.get(`/contact-requests/${id}`);
    return response.data;
  },
  updateStatus: async (id, data) => {
    const response = await api.put(`/contact-requests/${id}/status`, data);
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/contact-requests/${id}`);
    return response.data;
  },
  getStats: async () => {
    const response = await api.get('/contact-requests/stats');
    return response.data;
  }
};

export const audienceGroupService = {
  getAll: async () => {
    const response = await api.get('/audience-groups');
    return response.data;
  },
  create: async (data) => {
    const response = await api.post('/audience-groups', data);
    return response.data;
  },
  getContacts: async (id) => {
    const response = await api.get(`/audience-groups/${id}/contacts`);
    return response.data;
  },
  addContact: async (groupId, data) => {
    const response = await api.post(`/audience-groups/${groupId}/contacts`, data);
    return response.data;
  },
  updateContact: async (groupId, contactId, data) => {
    const response = await api.put(`/audience-groups/${groupId}/contacts/${contactId}`, data);
    return response.data;
  },
  deleteContact: async (groupId, contactId) => {
    const response = await api.delete(`/audience-groups/${groupId}/contacts/${contactId}`);
    return response.data;
  },
  update: async (id, data) => {
    const response = await api.put(`/audience-groups/${id}`, data);
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/audience-groups/${id}`);
    return response.data;
  }
};

export const faturamentoService = {
  listGoals: async (params = {}) => {
    const response = await api.get('/faturamento/goals', { params });
    return response.data;
  },
  createGoal: async (data) => {
    const response = await api.post('/faturamento/goals', data);
    return response.data;
  },
  updateGoal: async (id, data) => {
    const response = await api.put(`/faturamento/goals/${id}`, data);
    return response.data;
  },
  deleteGoal: async (id) => {
    const response = await api.delete(`/faturamento/goals/${id}`);
    return response.data;
  },
  saveGoalFilterConfig: async (goalId, filterId, data) => {
    const response = await api.post(`/faturamento/goals/${goalId}/filter-configs/${filterId}`, data);
    return response.data;
  },
  deleteGoalFilterConfig: async (goalId, filterId) => {
    const response = await api.delete(`/faturamento/goals/${goalId}/filter-configs/${filterId}`);
    return response.data;
  },
  copyGoalReferences: async (sourceGoalId, data) => {
    const response = await api.post(`/faturamento/goals/${sourceGoalId}/copy-references`, data);
    return response.data;
  },
  listSavedFilters: async (params = {}) => {
    const response = await api.get('/faturamento/filters', { params });
    return response.data;
  },
  createSavedFilter: async (data) => {
    const response = await api.post('/faturamento/filters', data);
    return response.data;
  },
  updateSavedFilter: async (id, data) => {
    const response = await api.put(`/faturamento/filters/${id}`, data);
    return response.data;
  },
  deleteSavedFilter: async (id) => {
    const response = await api.delete(`/faturamento/filters/${id}`);
    return response.data;
  },
  listReports: async (params = {}) => {
    const response = await api.get('/faturamento/reports', { params });
    return response.data;
  },
  createReport: async (data) => {
    const response = await api.post('/faturamento/reports', data);
    return response.data;
  },
  updateReport: async (id, data) => {
    const response = await api.put(`/faturamento/reports/${id}`, data);
    return response.data;
  },
  deleteReport: async (id) => {
    const response = await api.delete(`/faturamento/reports/${id}`);
    return response.data;
  },
  reprocessReport: async (id) => {
    const response = await api.post(`/faturamento/reports/${id}/reprocess`);
    return response.data;
  },
  finalizeReport: async (id) => {
    const response = await api.post(`/faturamento/reports/${id}/finalize`);
    return response.data;
  },
  bulkFinalizeReports: async (reportIds = []) => {
    const response = await api.post('/faturamento/reports/bulk-finalize', { report_ids: reportIds });
    return response.data;
  },
  exportReportsExcel: async (reportIds = []) => {
    const response = await api.post(
      '/faturamento/reports/export',
      { report_ids: reportIds },
      { responseType: 'blob' }
    );
    return response;
  },
  exportVariableExcel: async (reportId, variableId) => {
    const response = await api.post(
      '/faturamento/reports/export-variable',
      { report_id: reportId, variable_id: variableId },
      { responseType: 'blob' }
    );
    return response;
  },
  getMonitoring: async (params = {}) => {
    const response = await api.get('/faturamento/monitoring', { params });
    return response.data;
  },
  getBIYears: async () => {
    const response = await api.get('/faturamento/bi-metas/years');
    return response.data;
  },
  getBIMetas: async (params = {}) => {
    const response = await api.get('/faturamento/bi-metas', { params });
    return response.data;
  },
  getBIGoals: async (params = {}) => {
    const response = await api.get('/faturamento/bi-metas/goals', { params });
    return response.data;
  },
  getBIGoalOrders: async (params = {}) => {
    const response = await api.get('/faturamento/bi-metas/orders', { params });
    return response.data;
  },
  getBIProducts: async (params = {}) => {
    const response = await api.get('/faturamento/bi-metas/products', { params });
    return response.data;
  },
  getBIAIPrompt: async (data = {}) => {
    const response = await api.post('/faturamento/bi-metas/ai-prompt', data);
    return response.data;
  },
  getBIAIAnalysis: async (data = {}) => {
    const response = await api.post('/faturamento/bi-metas/ai-analysis', data);
    return response.data;
  },
  listVariables: async (params = {}) => {
    const response = await api.get('/faturamento/variables', { params });
    return response.data;
  },
  createVariable: async (data) => {
    const response = await api.post('/faturamento/variables', data);
    return response.data;
  },
  updateVariable: async (id, data) => {
    const response = await api.put(`/faturamento/variables/${id}`, data);
    return response.data;
  },
  deleteVariable: async (id) => {
    const response = await api.delete(`/faturamento/variables/${id}`);
    return response.data;
  },
  listEmployees: async (params = {}) => {
    const response = await api.get('/faturamento/employees', { params });
    return response.data;
  },
  listProducts: async (params = {}) => {
    const response = await api.get('/faturamento/products', { params });
    return response.data;
  },
  checkAccrualReports: async (data) => {
    const response = await api.post('/faturamento/variables/check-accrual-reports', data);
    return response.data;
  },
  removeAccrualReportReferences: async (data) => {
    const response = await api.post('/faturamento/variables/remove-accrual-reports', data);
    return response.data;
  },
  replaceAccrualReportReference: async (data) => {
    const response = await api.post('/faturamento/variables/replace-accrual-report', data);
    return response.data;
  },
  listGoalPresets: async (params = {}) => {
    const response = await api.get('/faturamento/goal-presets', { params });
    return response.data;
  },
  getGoalPreset: async (id) => {
    const response = await api.get(`/faturamento/goal-presets/${id}`);
    return response.data;
  },
  resolveGoalPreset: async (params = {}) => {
    const response = await api.get('/faturamento/goal-presets/resolve', { params });
    return response.data;
  },
  createGoalPreset: async (data) => {
    const response = await api.post('/faturamento/goal-presets', data);
    return response.data;
  },
  updateGoalPreset: async (id, data) => {
    const response = await api.put(`/faturamento/goal-presets/${id}`, data);
    return response.data;
  },
  deleteGoalPreset: async (id) => {
    const response = await api.delete(`/faturamento/goal-presets/${id}`);
    return response.data;
  },
  listSyncSchedules: async () => {
    const response = await api.get('/faturamento/sync-schedules');
    return response.data;
  },
  getSyncSchedule: async (id) => {
    const response = await api.get(`/faturamento/sync-schedules/${id}`);
    return response.data;
  },
  createSyncSchedule: async (data) => {
    const response = await api.post('/faturamento/sync-schedules', data);
    return response.data;
  },
  updateSyncSchedule: async (id, data) => {
    const response = await api.put(`/faturamento/sync-schedules/${id}`, data);
    return response.data;
  },
  deleteSyncSchedule: async (id) => {
    const response = await api.delete(`/faturamento/sync-schedules/${id}`);
    return response.data;
  },
  toggleSyncSchedule: async (id) => {
    const response = await api.post(`/faturamento/sync-schedules/${id}/toggle`);
    return response.data;
  },
  runSyncScheduleNow: async (id) => {
    const response = await api.post(`/faturamento/sync-schedules/${id}/run`);
    return response.data;
  },
  previewSyncSchedule: async (data) => {
    const response = await api.post('/faturamento/sync-schedules/preview', data);
    return response.data;
  }
};

export const vendasService = {
  getAll: async (params = {}) => {
    const response = await api.get('/faturamento/vendas', { params });
    return response.data;
  },
  getFiltersData: async () => {
    const response = await api.get('/faturamento/vendas/filters-data');
    return response.data;
  },
  getSearchSuggestions: async (params = {}) => {
    const response = await api.get('/faturamento/vendas/search-suggestions', { params });
    return response.data;
  },
  exportExcel: async (data = {}) => {
    const response = await api.post('/faturamento/vendas/export', data, { responseType: 'blob' });
    return response;
  }
};

export const filterDataService = {
  getDepartamentos: async (search = '') => {
    const response = await api.get('/filter-data/departamentos', { params: { search } });
    return response.data;
  },
  getAtividades: async (search = '') => {
    const response = await api.get('/filter-data/atividades', { params: { search } });
    return response.data;
  },
  getSecoes: async (search = '') => {
    const response = await api.get('/filter-data/secoes', { params: { search } });
    return response.data;
  },
  getMarcas: async (search = '') => {
    const response = await api.get('/filter-data/marcas', { params: { search } });
    return response.data;
  },
  getCategorias: async (search = '', codsec = '', limit = 100, offset = 0) => {
    const response = await api.get('/filter-data/categorias', { params: { search, codsec, limit, offset } });
    return response.data;
  },
  getFiliais: async (search = '') => {
    const response = await api.get('/filter-data/filiais', { params: { search } });
    return response.data;
  },
  getProdutos: async (search = '', limit = 100, offset = 0) => {
    const response = await api.get('/filter-data/produtos', { params: { search, limit, offset } });
    return response.data;
  },
  getVendedores: async (search = '', limit = 100, offset = 0, sortBy = 'nome', sortOrder = 'asc') => {
    const response = await api.get('/filter-data/vendedores', { params: { search, limit, offset, sort_by: sortBy, sort_order: sortOrder } });
    return response.data;
  },
  getSyncStatus: async () => {
    const response = await api.get('/filter-data/sync-status');
    return response.data;
  }
};

export const messageLogService = {
  getAll: async (params = {}) => {
    const response = await api.get('/message-logs', { params });
    return response.data;
  },
  getById: async (id) => {
    const response = await api.get(`/message-logs/${id}`);
    return response.data;
  },
  getCampaignSummary: async () => {
    const response = await api.get('/message-logs/campaign-summary');
    return response.data;
  },
  getDailyStats: async (days = 30) => {
    const response = await api.get('/message-logs/daily-stats', { params: { days } });
    return response.data;
  },
  clearAll: async () => {
    const response = await api.delete('/message-logs/clear-all', {
      timeout: 60000 // 60 segundos para operações grandes
    });
    return response.data;
  },
  bulkDelete: async (ids) => {
    const response = await api.delete('/message-logs/bulk-delete', {
      data: { ids }
    });
    return response.data;
  }
};

export const modulesService = {
  getAll: async () => {
    const response = await api.get('/running-modules');
    return response.data;
  },
  getById: async (id) => {
    const response = await api.get(`/running-modules/${id}`);
    return response.data;
  },
  getStats: async () => {
    const response = await api.get('/running-modules/stats');
    return response.data;
  },
  cleanup: async () => {
    const response = await api.post('/running-modules/cleanup');
    return response.data;
  }
};

export const aiService = {
  getProviders: async () => {
    const response = await api.get('/ai/providers');
    return response.data;
  },
  createProvider: async (data) => {
    const response = await api.post('/ai/providers', data);
    return response.data;
  },
  updateProvider: async (id, data) => {
    const response = await api.put(`/ai/providers/${id}`, data);
    return response.data;
  },
  deleteProvider: async (id) => {
    const response = await api.delete(`/ai/providers/${id}`);
    return response.data;
  },
  testProvider: async (id) => {
    const response = await api.post(`/ai/providers/${id}/test`);
    return response.data;
  },
  listProviderModels: async (id) => {
    const response = await api.get(`/ai/providers/${id}/models`);
    return response.data;
  },
  listModelsFromProvider: async (data) => {
    const response = await api.post('/ai/providers/list-models', data);
    return response.data;
  },

  executeAction: async (data) => {
    const response = await api.post('/ai/execute', data, {
      timeout: 300000 // 5 minutos para processamento IA
    });
    return response.data;
  },
  checkAiResult: async (conversationId) => {
    const response = await api.get(`/ai/check-result/${conversationId}`);
    return response.data;
  },
  undoAction: async (logId) => {
    const response = await api.post(`/ai/undo/${logId}`);
    return response.data;
  },
  getActionLog: async (params = {}) => {
    const response = await api.get('/ai/action-log', { params });
    return response.data;
  },
  getActionLogDetail: async (id) => {
    const response = await api.get(`/ai/action-log/${id}`);
    return response.data;
  },
  getSystemPrompt: async () => {
    const response = await api.get('/ai/system-prompt');
    return response.data;
  },
  updateSystemPrompt: async (systemPrompt) => {
    const response = await api.put('/ai/system-prompt', { system_prompt: systemPrompt });
    return response.data;
  },

  // Conversations
  conversationsList: async () => {
    const response = await api.get('/ai/conversations');
    return response.data;
  },
  conversationsCreate: async (data = {}) => {
    const response = await api.post('/ai/conversations', data);
    return response.data;
  },
  conversationsGet: async (id) => {
    const response = await api.get(`/ai/conversations/${id}`);
    return response.data;
  },
  conversationsUpdate: async (id, data) => {
    const response = await api.put(`/ai/conversations/${id}`, data);
    return response.data;
  },
  conversationsDelete: async (id) => {
    const response = await api.delete(`/ai/conversations/${id}`);
    return response.data;
  },

  // File upload
  uploadAttachment: async (file, conversationId) => {
    const formData = new FormData();
    formData.append('file', file);
    if (conversationId) {
      formData.append('conversation_id', conversationId);
    }
    const response = await api.post('/ai/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },
  deleteAttachment: async (id) => {
    const response = await api.delete(`/ai/attachments/${id}`);
    return response.data;
  },

  // Prompt Templates
  getPromptTemplates: async () => {
    const response = await api.get('/ai/prompt-templates');
    return response.data;
  },
  createPromptTemplate: async (data) => {
    const response = await api.post('/ai/prompt-templates', data);
    return response.data;
  },
  updatePromptTemplate: async (id, data) => {
    const response = await api.put(`/ai/prompt-templates/${id}`, data);
    return response.data;
  },
  deletePromptTemplate: async (id) => {
    const response = await api.delete(`/ai/prompt-templates/${id}`);
    return response.data;
  },

  // OCR settings
  getOcrSpaceKey: async () => {
    const response = await api.get('/ai/settings/ocr-space-key');
    return response.data;
  },
  updateOcrSpaceKey: async (apiKey) => {
    const response = await api.put('/ai/settings/ocr-space-key', { ocr_space_api_key: apiKey });
    return response.data;
  },

  // Functions
  getFunctions: async () => {
    const response = await api.get('/ai/functions');
    return response.data;
  }
};

export default api;
